import React from 'react'

/** Fallback when intl is unavailable (e.g. PDF payload build with `intl: null`). */
const FALLBACK_MESSAGE_TEXT = {
  'UIC.GENERAL.FALSE': 'False',
  'UIC.GENERAL.NOT': 'not',
  'UIC.GENERAL.TRUE': 'True',
}

/**
 * Coerces export/table cell values to plain text. Handles strings, arrays, React elements
 * (including FormattedMessage with nested `values`), and JSON-serialized element shapes
 * from status detail formatters.
 */
export const normalizeComplianceAuditCellValue = (value, intl) => {
  const formatMessageId = (id, values) => {
    if (!id) {
      return ''
    }

    const normalizedValues =
      values && typeof values === 'object'
        ? Object.fromEntries(
            Object.entries(values).map(([key, entry]) => [key, normalizeComplianceAuditCellValue(entry, intl)]),
          )
        : undefined

    if (intl) {
      try {
        const formatted = intl.formatMessage({ id }, normalizedValues)
        // After normalizing nested FormattedMessage values to strings, formatMessage should
        // return plain text. Recurse if a rich-text node slips through.
        if (typeof formatted === 'string' || typeof formatted === 'number') {
          return String(formatted)
        }

        if (formatted == null) {
          return ''
        }

        return normalizeComplianceAuditCellValue(formatted, intl)
      } catch {
        // ignore missing message
      }
    }

    return FALLBACK_MESSAGE_TEXT[id] ?? id
  }

  if (value == null || value === '') {
    return ''
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  if (typeof value === 'boolean') {
    return String(value)
  }

  if (React.isValidElement(value)) {
    if (value.props?.id) {
      return formatMessageId(value.props.id, value.props.values)
    }

    if (value.props?.children != null) {
      return normalizeComplianceAuditCellValue(value.props.children, intl)
    }

    return ''
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeComplianceAuditCellValue(item, intl)).join('')
  }

  if (typeof value === 'object' && value.props?.id) {
    return formatMessageId(value.props.id, value.props.values)
  }

  return ''
}

/** Maps localized status labels (from export/PDF rows) to Badge variants aligned with posture colors. */
const STATUS_LABELS_BY_VARIANT = {
  success: ['SECURE.POSTURE.CONFIGURED'],
  warning: ['SECURE.POSTURE.PARTIALLY_CONFIGURED'],
  danger: ['SECURE.POSTURE.NOT_CONFIGURED', 'SECURITY_CHECK.STATE.RISK_ACCEPTED'],
  primary: [
    'SECURE.POSTURE.RESOLVED',
    'SECURITY_CHECK.STATE.THIRD_PARTY',
    'SECURITY_CHECK.STATE.ALTERNATE_MITIGATION',
    'SECURITY_CHECK.STATE.PLANNED',
    'SECURITY_CHECK.STATE.AUGMENTT_COMPLIANCE',
  ],
  secondary: ['SECURE.POSTURE.NOT_MEASURED', 'SECURE.POSTURE.IGNORED'],
}

export const getComplianceAuditStatusBadgeVariant = (formattedStatus, intl) => {
  if (!formattedStatus) {
    return 'secondary'
  }

  const entries = Object.entries(STATUS_LABELS_BY_VARIANT)
  const match = entries.find(([, messageIds]) =>
    messageIds.some((id) => formattedStatus === intl.formatMessage({ id })),
  )

  return match?.[0] ?? 'secondary'
}

export const formatComplianceAuditTemplates = (formattedTemplates) => {
  if (Array.isArray(formattedTemplates)) {
    return formattedTemplates.join(', ')
  }

  return formattedTemplates ?? ''
}
